import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { humanise, inr, num } from '../lib/format';
import { Card, CardHead, Chips, Empty } from '../components/ui';
import { BarList, LineChart } from '../components/Chart';

interface Analytics {
  days: number;
  series: Array<{ day: string; signups: number; listings: number; payments: number; revenuePaise: number; bookings: number }>;
  listingsByStatus: Array<{ status: string; count: number }>;
  categoriesBySector: Array<{ sector: string; count: number }>;
  topCategories: Array<{ name: string; count: number }>;
}

export function AnalyticsPage() {
  const [days, setDays] = useState('30');
  const { data, isLoading } = useQuery({
    queryKey: ['analytics', days],
    queryFn: () => api.get<Analytics>(`/admin/analytics?days=${days}`),
  });

  const total = (key: keyof Analytics['series'][number]) =>
    data?.series.reduce((n, d) => n + (d[key] as number), 0) ?? 0;

  return (
    <div className="stack" style={{ gap: 'var(--s5)' }}>
      <Chips
        value={days}
        onChange={setDays}
        options={[
          { value: '7', label: '7 days' },
          { value: '30', label: '30 days' },
          { value: '90', label: '90 days' },
          { value: '365', label: '1 year' },
        ]}
      />

      <div className="grid grid--stats">
        {[
          { label: 'Signups', value: num(total('signups')), tint: 'var(--tint-blue)', color: 'var(--blue)', icon: '☖' },
          { label: 'New listings', value: num(total('listings')), tint: 'var(--tint-orange)', color: 'var(--orange-deep)', icon: '▤' },
          { label: 'Bookings', value: num(total('bookings')), tint: 'var(--tint-green)', color: 'var(--success)', icon: '✆' },
          { label: 'Revenue', value: inr(total('revenuePaise')), tint: 'var(--tint-purple)', color: 'var(--purple)', icon: '₹' },
        ].map((s) => (
          <Card key={s.label}>
            <div className="stat">
              <div className="stat__icon" style={{ background: s.tint, color: s.color }}>
                {s.icon}
              </div>
              <div>
                <div className="stat__label">{s.label}</div>
                <div className="stat__value">{isLoading ? '—' : s.value}</div>
                <div className="stat__delta stat__delta--flat">last {days} days</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Card>
        <CardHead title="Activity" sub="Signups, listings and captured payments per day" />
        <div style={{ paddingTop: 'var(--s4)' }}>
          {data ? (
            <LineChart
              labels={data.series.map((s) => s.day)}
              series={[
                { label: 'Signups', color: 'var(--blue)', values: data.series.map((s) => s.signups) },
                { label: 'Listings', color: 'var(--orange)', values: data.series.map((s) => s.listings) },
                { label: 'Bookings', color: 'var(--success)', values: data.series.map((s) => s.bookings) },
              ]}
            />
          ) : (
            <div className="skeleton" style={{ height: 210 }} />
          )}
        </div>
      </Card>

      <Card>
        <CardHead title="Revenue" sub="Captured payments per day, in rupees" />
        <div style={{ paddingTop: 'var(--s4)' }}>
          {data ? (
            <LineChart
              labels={data.series.map((s) => s.day)}
              series={[
                { label: 'Revenue (₹)', color: 'var(--purple)', values: data.series.map((s) => Math.round(s.revenuePaise / 100)) },
              ]}
            />
          ) : (
            <div className="skeleton" style={{ height: 210 }} />
          )}
        </div>
      </Card>

      <div className="grid grid--2">
        <Card>
          <CardHead title="Listings by status" />
          <div style={{ paddingTop: 'var(--s4)' }}>
            {data?.listingsByStatus.length ? (
              <BarList rows={data.listingsByStatus.map((r) => ({ label: humanise(r.status), value: r.count }))} />
            ) : (
              <Empty icon="▤" title="No listings yet" />
            )}
          </div>
        </Card>

        <Card>
          <CardHead title="Categories by sector" />
          <div style={{ paddingTop: 'var(--s4)' }}>
            {data?.categoriesBySector.length ? (
              <BarList rows={data.categoriesBySector.map((r) => ({ label: humanise(r.sector), value: r.count }))} />
            ) : (
              <Empty icon="⌗" title="No categories yet" />
            )}
          </div>
        </Card>
      </div>

      <Card>
        <CardHead title="Busiest categories" sub="By live listings" />
        <div style={{ paddingTop: 'var(--s4)' }}>
          {data?.topCategories.length ? (
            <BarList rows={data.topCategories.map((r) => ({ label: r.name, value: r.count }))} />
          ) : (
            <Empty icon="⌗" title="No live listings yet" />
          )}
        </div>
      </Card>
    </div>
  );
}
